<script setup lang="ts">
import { Mask } from 'maska';
import type { UserDocument } from '@/types/UserData';
import type Button from '@/components/auth/verification/Box/Proceed.vue';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { isCaptchaRegisterEnabled, isCaptchaValidateCpfEnabled } = useCaptcha();

const {
  userEmail,
  userHasDocument,
  userDocument,
  userHasBirthdate,
  userBirthdate,
  userGender,
  genderOptions,
  userNationalities,
  fetchUserProfile,
  isRegulatoryValidationEnabled,
} = useUserData();

const documentField = ref<string>('');
const isLoadingRegisterPage = ref<boolean>(false);

export type RegisterData = {
  name: string;
  nationalities: any[];
  birth_date?: string;
  document: string;
  cpf?: string;
  gender?: number | string;
};

type ValidationFields = Record<keyof RegisterData, boolean>;

export type ValidationData = {
  errors: number;
  fields: ValidationFields;
};

const docsData = ref<RegisterData>({
  name: '',
  nationalities: [],
  birth_date: '',
  document: '',
  gender: undefined,
});

const validationData = ref<ValidationData>({
  errors: 0,
  fields: {
    gender: false,
    name: false,
    nationalities: false,
    birth_date: false,
    document: false,
    cpf: false,
  },
});

const isLoading = ref(false);

const proceedButtonRef = ref<InstanceType<typeof Button> | null>(null);

const antiRobotToken = computed<string>(
  () => proceedButtonRef.value?.antiRobotToken ?? '',
);

function resetAntiRobotToken() {
  proceedButtonRef.value?.$resetAllCaptcha();
}

const nationalities = [
  { id: 1, value: 'Brasileiro (a)' },
  { id: 2, value: 'Alemão (a)' },
  { id: 3, value: 'Americano (a)' },
  { id: 4, value: 'Argentino (a)' },
  { id: 5, value: 'Canadense (a)' },
  { id: 6, value: 'Chileno (a)' },
  { id: 7, value: 'Espanhol (a)' },
  { id: 8, value: 'Francês (a)' },
  { id: 9, value: 'Indiano (a)' },
  { id: 10, value: 'Italiano (a)' },
  { id: 11, value: 'Japonês (a)' },
  { id: 12, value: 'Mexicano (a)' },
  { id: 13, value: 'Português (a)' },
  { id: 14, value: 'Russo (a)' },
  { id: 15, value: 'Sueco (a)' },
  { id: 16, value: 'Suíço (a)' },
  { id: 17, value: 'Outro' },
];

const unmaskedCpf = computed<string>(() => {
  const mask = new Mask({ mask: '###.###.###-##' });

  return mask.unmasked(documentField.value);
});

function handleSelectedDate(formattedDate: any) {
  if (typeof formattedDate !== 'string' || formattedDate === '') {
    docsData.value.birth_date = '';
    validationData.value.fields.birth_date = false;

    return;
  }

  updateSelectedDate(formattedDate);
}

function updateSelectedDate(formattedDate: any) {
  docsData.value.birth_date = formattedDate;
  docsData.value.birth_date
    ? (validationData.value.fields.birth_date = true)
    : (validationData.value.fields.birth_date = false);
}

function onDocumentRemoved() {
  docsData.value.name = '';
  validationData.value.fields.name = false;
  docsData.value.document = '';
  validationData.value.fields.document = false;

  if (isCaptchaRegisterEnabled.value || isCaptchaValidateCpfEnabled.value) {
    resetAntiRobotToken();
  }
}

function onDocumentValidated(data: UserDocument) {
  if (data.name) {
    docsData.value.name = data.name;
    validationData.value.fields.name = true;
  }
  docsData.value.document = unmaskedCpf.value;
  validationData.value.fields.document = true;
}

const isNextStepDisabled = computed(() => {
  if (isLoadingRegisterPage.value) return true;

  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    return true;
  }

  if (!userHasDocument.value) {
    if (!docsData.value.document) return true;
  }

  if (!userHasBirthdate.value) {
    if (!docsData.value.birth_date) return true;
  }

  if (!userNationalities.value || userNationalities.value.length === 0) {
    if (!docsData.value.nationalities.length) return true;
  }

  if (isRegulatoryValidationEnabled.value && !userGender.value) {
    if (!docsData.value.gender) return true;
  }

  return false;
});

function checkNationalities() {
  if (docsData.value.nationalities.length === 0) {
    validationData.value.fields.nationalities = false;

    return;
  }
  validationData.value.fields.nationalities = true;
}

function handleSuccess(
  res: { status: number; data: { success: boolean; message: string } },
  successCallback?: (res: any) => void,
) {
  if (res.status.toString().startsWith('20') && res.data.success) {
    successCallback?.(res);
  } else {
    const message = res.data.message || t('auth.register.error.status_error');

    openToaster({
      message,
      type: 'error',
    });
  }
}

function handleError(err: any, fallbackMessageKey: string) {
  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  openToaster({
    message: errorMessage,
    type: 'error',
  });
}

async function onSubmitUpdateProfile() {
  if (isNextStepDisabled.value) {
    return;
  }

  isLoading.value = true;

  try {
    const { ApiService } = useApiService();

    const payload: Partial<RegisterData> = {
      nationalities: docsData.value.nationalities[0],
    };

    if (!userHasDocument.value) {
      payload.cpf = docsData.value.document;
    }

    if (!userHasBirthdate.value) {
      payload.birth_date = docsData.value.birth_date;
    }

    if (isRegulatoryValidationEnabled.value) {
      payload.gender = Number(docsData.value.gender);
    }

    const res = await ApiService.patch(
      '/bff/users/update-pending-data',
      payload,
    );

    handleSuccess(res, async () => {
      await fetchUserProfile({ force: true });

      await $containment.solve({
        name: 'docs',
        customValidator: () => res.data.success,
      });
    });
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');
  } finally {
    isLoading.value = false;
  }
}

watch(() => docsData.value.nationalities, checkNationalities);

onMounted(() => {
  docsData.value.nationalities = [nationalities[0]];

  if (userGender.value) {
    docsData.value.gender = userGender.value;
  }
});
</script>

<template>
  <AuthVerificationBox :is-loading="isLoading">
    <div :class="$style.multiStepForm">
      <BaseLoading
        class="!bg-auth-bg-primary"
        :icon-loader="true"
        :show="isLoadingRegisterPage"
      />
      <label for="email">
        {{ $t('auth.register.email') }} <span>*</span>
      </label>
      <BaseFormInput
        id="email"
        v-model="userEmail"
        autocomplete="off"
        disabled
        :initial-value="userEmail"
        name="email"
        type="email"
        :with-check="true"
      />

      <label for="cpf"> {{ $t('auth.register.cpf') }} <span>*</span> </label>
      <BaseFormInput
        v-if="userHasDocument"
        id="cpf"
        v-model="userDocument"
        autocomplete="off"
        disabled
        :initial-value="userDocument"
        name="cpf"
        type="text"
        :with-check="true"
      />
      <BaseFormInputCpf
        v-else
        id="cpf"
        v-model:masked="documentField"
        :anti-robot-token="antiRobotToken ?? ''"
        hide-icon
        name="cpf"
        placeholder="ex: 123.456.789-00"
        required
        @remove-document="onDocumentRemoved"
        @reset-anti-robot-token="resetAntiRobotToken()"
        @validate-document="onDocumentValidated"
      />

      <label for="birth_date">
        {{ $t('auth.register.birthdate') }} <span>*</span>
      </label>
      <BaseFormSelectBirthdate
        id="birth_date"
        v-model="docsData.birth_date"
        :disabled="userHasBirthdate"
        :icon="false"
        :initial-value="userBirthdate"
        :name="'birth_date'"
        :required="true"
        :separate-fields="true"
        @keydown.enter.prevent=""
        @update:selected-date="handleSelectedDate"
      />

      <label for="nationalities">
        {{ $t('auth.register.nationalities') }} <span>*</span>
      </label>
      <BaseFormSelect
        id="nationalities"
        v-model="docsData.nationalities"
        class="w-full"
        multiple
        open-up
        :options="nationalities"
      >
      </BaseFormSelect>
      <div
        v-if="isRegulatoryValidationEnabled"
        :class="$style.gender"
      >
        <label> {{ $t('verification.docs.gender') }} <span>*</span> </label>
        <p :class="$style.genderDesc">{{
          $t('verification.docs.genderDesc')
        }}</p>
        <div :class="$style.customRadioGroup">
          <BaseFormRadio
            v-model="docsData.gender"
            :options="genderOptions"
          />
        </div>
      </div>
    </div>

    <AuthVerificationBoxProceed
      ref="proceedButtonRef"
      :disabled="isNextStepDisabled"
      :is-docs-step="true"
      @submit="onSubmitUpdateProfile"
    />
  </AuthVerificationBox>
</template>

<style module lang="scss">
.multiStepForm {
  @apply relative w-full flex flex-col gap-5 text-left;

  label {
    @apply -mb-4 text-xs text-texts font-semibold;

    span {
      @apply text-error;
    }
  }
  .genderDesc {
    @apply mb-2 pb-2 inline-block text-xs text-texts/80;
  }
  .customRadioGroup {
    @apply pb-8;
    > div {
      @apply gap-0;
    }
  }
}
</style>
